"use client"

import { useCallback, useState } from "react"

import { usePwaStore } from "./store/pwa"
import { useHydrated } from "./use-hydrated"

const SESSION_DISMISS_KEY = "psc-sw-update-dismissed"

/**
 * Drives the sticky-top "Update available" banner.
 *
 * `updateReady` flips to true when components/service-worker.tsx detects a
 * NEW waiting SW that's an update (not the first install). `applyUpdate()`
 * tells the waiting SW to skipWaiting; the controllerchange listener in
 * service-worker.tsx then reloads the page so the new SW takes over.
 * `dismiss()` hides the banner for the current session only.
 */
export function useServiceWorkerUpdate() {
  const hydrated = useHydrated()
  const swUpdateReady = usePwaStore((s) => s.swUpdateReady)

  // Lazy-init read of sessionStorage avoids both setState-in-effect and
  // accessing window during SSR (typeof check). Visibility is gated on
  // `hydrated` so the first client paint still matches SSR markup.
  const [sessionDismissed, setSessionDismissed] = useState(() => {
    if (typeof window === "undefined") return false
    return window.sessionStorage.getItem(SESSION_DISMISS_KEY) === "1"
  })

  const applyUpdate = useCallback(() => {
    if (typeof navigator === "undefined" || !("serviceWorker" in navigator))
      return
    navigator.serviceWorker.ready
      .then((reg) => {
        reg.waiting?.postMessage({ type: "SKIP_WAITING" })
      })
      .catch(() => {
        // The controllerchange listener won't fire if the post fails. Worst
        // case the banner stays visible; user can hard-refresh.
      })
  }, [])

  const dismiss = useCallback(() => {
    if (typeof window !== "undefined") {
      sessionStorage.setItem(SESSION_DISMISS_KEY, "1")
    }
    setSessionDismissed(true)
  }, [])

  return {
    updateReady: hydrated && swUpdateReady && !sessionDismissed,
    applyUpdate,
    dismiss,
  }
}
